import { Plus } from "lucide-react";
import type React from "react";
import { useState } from "react";
import { Button } from "src/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "src/components/ui/dialog";
import type { AmortizationPlan, CorrectionPlan } from "../types";
import { AmortizationPlanForm } from "./AmortizationPlanForm";
import { CorrectionPlanForm } from "./CorrectionPlanForm";
import { PlanList } from "./PlanList";

interface AmortizationSectionProps {
  amortizationPlans: AmortizationPlan[];
  setAmortizationPlans: (plans: AmortizationPlan[]) => void;
  correctionPlans: CorrectionPlan[];
  setCorrectionPlans: (plans: CorrectionPlan[]) => void;
  onError: (message: string) => void;
}

export const AmortizationSection: React.FC<AmortizationSectionProps> = ({
  amortizationPlans,
  setAmortizationPlans,
  correctionPlans,
  setCorrectionPlans,
  onError,
}) => {
  const [isAmortizationOpen, setIsAmortizationOpen] = useState(false);
  const [isCorrectionOpen, setIsCorrectionOpen] = useState(false);

  const handleAddAmortization = (plan: AmortizationPlan) => {
    setAmortizationPlans([...amortizationPlans, plan]);
    setIsAmortizationOpen(false);
    onError("");
  };

  const handleAddCorrection = (plan: CorrectionPlan) => {
    setCorrectionPlans([...correctionPlans, plan]);
    setIsCorrectionOpen(false);
    onError("");
  };

  return (
    <div className="mt-8 border-t pt-6">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-4 gap-4">
        <h3 className="font-semibold text-lg flex items-center gap-2">
          Plano de amortização & Correção
          <span className="inline-flex items-center rounded-full bg-primary/10 px-2 py-0.5 text-xs font-semibold text-primary ring-1 ring-inset ring-primary/20">
            Novo
          </span>
        </h3>
        <div className="flex gap-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => setIsAmortizationOpen(true)}
            className="text-green-600 border-green-200 bg-green-50 hover:bg-green-100 hover:text-green-700"
          >
            <Plus className="w-4 h-4 mr-1" /> Adicionar Plano
          </Button>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => setIsCorrectionOpen(true)}
            className="text-green-600 border-green-200 bg-green-50 hover:bg-green-100 hover:text-green-700"
          >
            <Plus className="w-4 h-4 mr-1" /> Correção
          </Button>
        </div>
      </div>

      <PlanList
        amortizationPlans={amortizationPlans}
        correctionPlans={correctionPlans}
        onRemoveAmortization={(id) =>
          setAmortizationPlans(amortizationPlans.filter((p) => p.id !== id))
        }
        onRemoveCorrection={(id) =>
          setCorrectionPlans(correctionPlans.filter((p) => p.id !== id))
        }
      />

      <Dialog open={isAmortizationOpen} onOpenChange={setIsAmortizationOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Adicionar plano de amortização</DialogTitle>
          </DialogHeader>
          <AmortizationPlanForm
            onAdd={handleAddAmortization}
            onCancel={() => setIsAmortizationOpen(false)}
            onError={onError}
          />
        </DialogContent>
      </Dialog>

      <Dialog open={isCorrectionOpen} onOpenChange={setIsCorrectionOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Adicionar correção monetária</DialogTitle>
          </DialogHeader>
          <CorrectionPlanForm
            onAdd={handleAddCorrection}
            onCancel={() => setIsCorrectionOpen(false)}
            onError={onError}
          />
        </DialogContent>
      </Dialog>
    </div>
  );
};
